import { useDucatClient } from '@/context/DucatClientCtx';
import { computeLiquidationPrice, computeUnitBalance, computeVaultUtxoFromTx } from '@/utils/vault.utils';
import { VaultPrevout, VaultProfile, VaultToken } from '@ducat-unit/client-sdk';
import { useEffect, useMemo } from 'react';
import { useQueryVaultHistory } from '../api/useQueryVaultHistory';
import { useQueryAccountVaultList } from '../wallet/useQueryAccountVaultList';
import { normalize_i0 } from '../wallet/utils';

export type VaultCtx = {
  profile: VaultProfile;
  token: VaultToken;
};

export function useVaultProfile() {
  const { wallet } = useDucatClient();
  const vaultPubkey = wallet?.acct.runes.pubkey;

  // Vault info data from validator:
  const { data: accountVaultList, isLoading } = useQueryAccountVaultList(vaultPubkey);

  const validatorVault = accountVaultList?.vaults[0];
  const tag = validatorVault?.vault_tag;
  const vid = validatorVault?.vault_id + 'i1';
  const vaultTxId = validatorVault?.vault_id;
  const vaultUnitBalance = validatorVault ? validatorVault.unit_borrowed * 100 : undefined;
  const vaultSatsLocked = validatorVault ? validatorVault.btc_locked * 100_000_000 : undefined;

  // Vault transactions from validator:
  const { data: vaultHistoryTxs, isLoading: isLoadingVaultHistory } = useQueryVaultHistory(vaultTxId);
  const latestTx = vaultHistoryTxs?.[0];
  const vaultPrevout: VaultPrevout | undefined = latestTx && computeVaultUtxoFromTx(latestTx);

  const profile: VaultProfile | undefined = useMemo(() => {
    if (!vaultPubkey || !validatorVault || !vaultPrevout) {
      return;
    }

    return {
      // This data is created with the vault, and doesn't change
      acct_id: validatorVault.creation_account,
      guard_pk: validatorVault.guard_pubkey,
      master_id: normalize_i0(validatorVault.master_id),
      vault_pk: vaultPubkey,

      // This data changes with every vault action
      rdata: vaultPrevout.rdata,
      utxo: vaultPrevout.utxo,
    };
  }, [vaultPubkey, validatorVault, vaultPrevout]);

  const satsInVault = vaultSatsLocked ?? vaultPrevout?.utxo.value ?? 0;
  const btcInVault = satsInVault / 100_000_000;

  // Profile may be outdated so check newer vault history transactions to compute final unit balance.
  const unit_balance = useMemo(() => {
    return vaultUnitBalance ?? profile?.rdata.unit_balance ?? 0;
    // return computeUnitBalance(profile, vaultHistoryTxs);
  }, [profile, vaultHistoryTxs]);

  // Unit dollars in vault:
  const unitInVault = unit_balance / 100;

  const liquidationPrice = computeLiquidationPrice(unitInVault, btcInVault);

  return {
    vaultProfile: profile,
    tag,
    vaultPubkey,
    vid,
    vaultTxId,
    isLoading: isLoading || isLoadingVaultHistory,
    unit_balance,
    satsInVault,
    btcInVault,
    unitInVault,
    liquidationPrice,
    vaultPrevout,
  };
}
